import { useState } from "react";
import type { PropsWithChildren } from "react";

import noop from "lodash/noop";
import type { LayoutChangeEvent } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { VStack } from "@/ui/layout";
import type { StackProps } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { isWeb } from "@/utils/platform.util";

interface BottomSheetModalContentContainerProps extends PropsWithChildren<ViewWithTestID> {
  gap?: StackProps["gap"];
  isScrollable?: boolean;
  isSmallBottomPadding?: boolean;
  maxHeight?: StackProps["maxHeight"];
  shouldResizeOnContentChange?: boolean;
}

const BottomSheetModalContentContainer = ({
  children,
  gap,
  isScrollable,
  isSmallBottomPadding,
  maxHeight,
  shouldResizeOnContentChange,
  testID,
}: BottomSheetModalContentContainerProps) => {
  const { bottom: bottomInset } = useSafeAreaInsets();

  const [contentHeight, setContentHeight] = useState<number | undefined>();

  const basePadding = 24;
  const smallBottomPadding = 18;
  const normalBottomPadding =
    bottomInset > 0 ? bottomInset + basePadding / 2 : basePadding;
  const bottomPadding = isSmallBottomPadding
    ? smallBottomPadding
    : normalBottomPadding;

  const handleContentLayout = (event: LayoutChangeEvent) => {
    if (contentHeight || shouldResizeOnContentChange) {
      return;
    }

    // Capture the height of the content in the initial render to prevent the modal container size to change
    // if the content height changes. ( e.g. the modal content is a list of search results )
    // When the content height is already set, do not update it.
    // contentHeight is set to undefined when the modal is dismissed.

    const { height } = event.nativeEvent.layout;
    setContentHeight(height);
  };

  return (
    <VStack
      backgroundColor={ColorToken.NeutralBackgroundPrimary}
      borderTopLeftRadius={16}
      borderTopRightRadius={40}
      flex={isScrollable && !isWeb() ? 1 : undefined}
      gap={gap}
      height={isScrollable ? maxHeight : contentHeight || "auto"}
      maxHeight={maxHeight}
      onLayout={handleContentLayout}
      // Capture touch events in the alert to prevent background dismiss
      onPress={noop}
      paddingBottom={bottomPadding}
      paddingHorizontal={basePadding}
      paddingTop={24}
      testID={testID}
    >
      {children}
    </VStack>
  );
};

export default BottomSheetModalContentContainer;
